import { Controller } from "@hotwired/stimulus";
import heic2any from "heic2any";
import Compressor from "compressorjs";

let uppyModule;

export default class extends Controller {
  static targets = ["dropzone", "thumbnail", "uploadButton", "form", "file"];
  static values = {
    formFieldPrefix: String,
    allowedFileTypes: Array,
  };

  initialize() {
    this.numPendingRequests = 0;

    uppyModule = import("~/lib/uppy");
  }

  async createUppy() {
    const Uppy = (await uppyModule).uppyInstance;
    const uploadedFileData = (await uppyModule).uploadedFileData;
    const nanoid = (await uppyModule).nanoid;
    const formFieldPrefix = this.formFieldPrefixValue;
    const initialUploadButtonInnerHTML = this.uploadButtonTarget.innerHTML;

    this.uppy = new Uppy({
      id: this.dropzoneTarget.id,
      autoProceed: true,
      restrictions: {
        maxFileSize: 10000000,
        allowedFileTypes: this.allowedFileTypesValue,
      },
      dropTarget: this.dropzoneTarget,
      statusBarTarget: this.statusBarTarget,
      onBeforeFileAdded: this.onBeforeFileAdded.bind(this),
    });

    this.uppy.on("restriction-failed", (_file, error) => {
      // Suppress error for known intentional skip
      if (error?.message?.includes("onBeforeFileAdded returned false")) return;
      alert(`Error: ${error.message}`);
    });

    this.uppy.on("thumbnail:generated", (file, preview) => {
      this.addThumbnail(file, preview);
    });

    this.uppy.on("upload", (data) => {
      this.numPendingRequests += data.fileIDs.length;
      this.disableUploadButton();
    });

    this.uppy.on("progress", (progress) => {
      // Skip progressBar at 0% - avoids showing a tiny dot for fast uploads
      if (progress == 0) {
        return;
      } else if (this.progressBar) {
        this.progressBar.style.setProperty("--du-value", progress);
        this.progressBar.setAttribute("aria-valuenow", progress);
      } else {
        this.progressBar = document.createElement("div");
        this.progressBar.classList.add("du-radial-progress", "text-label");
        this.progressBar.setAttribute("style", `--du-value: ${progress};--du-size: 16px;--du-thickness: 3px;`);
        this.progressBar.setAttribute("aria-valuenow", progress);
        this.progressBar.setAttribute("role", "progressbar");
        this.uploadButtonTarget.innerHTML = "";
        this.uploadButtonTarget.appendChild(this.progressBar);
      }
    });

    const hiddenFieldContainerClass = "js-fileUploadHiddenFields";
    this.hiddenFieldContainer = this.formTarget.querySelector(`.${hiddenFieldContainerClass}`);

    if (!this.hiddenFieldContainer) {
      this.hiddenFieldContainer = document.createElement("div");
      this.hiddenFieldContainer.classList.add(hiddenFieldContainerClass);
      this.formTarget.appendChild(this.hiddenFieldContainer);
    }

    this.uppy.on("upload-success", (file, _response) => {
      const id = nanoid();
      const hiddenField = document.createElement("input");
      hiddenField.type = "hidden";

      // Use different attribute name based on file type
      const attributeName = file.type === "application/pdf" ? "documents" : "images";
      hiddenField.name = `${formFieldPrefix}[${attributeName}_attributes][${id}][file]`;

      hiddenField.value = uploadedFileData(file);
      this.hiddenFieldContainer.appendChild(hiddenField);

      if (file.type === "application/pdf") {
        this.addPdfPreview(file);
      }

      this.numPendingRequests -= 1;
      if (this.numPendingRequests === 0) {
        this.uploadButtonTarget.disabled = false;
        this.uploadButtonTarget.classList.remove("du-btn-disabled");
        this.progressBar.remove();
        this.progressBar = null;
        this.uploadButtonTarget.innerHTML = initialUploadButtonInnerHTML;
      }
    });

    this.formTarget.addEventListener("turbo:submit-end", () => {
      this.uppy.cancelAll();
      this.formTarget.reset();
      this.thumbnailTarget.classList.add("hidden");
      this.thumbnailTarget.innerHTML = "";
      this.hiddenFieldContainer.innerHTML = "";
    });

    if (this.fileTarget) {
      this.fileTarget.addEventListener("change", (event) => {
        const files = Array.from(event.target.files);

        files.forEach((file) => {
          try {
            this.uppy.addFile({
              source: "file input",
              name: file.name,
              type: file.type,
              data: file,
            });
          } catch (err) {
            if (!err.isRestriction) {
              console.error(err);
            }
          }
        });
      });

      this.uppy.on("file-removed", () => {
        this.fileTarget.value = null;
      });

      this.uppy.on("complete", () => {
        this.fileTarget.value = null;
      });
    }
  }

  addThumbnail(file, thumbnail) {
    const image = new Image();
    image.src = thumbnail;
    image.classList.add("max-h-40");
    this.thumbnailTarget.appendChild(image);
    this.thumbnailTarget.classList.remove("hidden");
    this.scrollThumbnailContainer();
  }

  // Debounce scroll to ensure we scroll only after a burst of thumbnail additions completes
  scrollThumbnailContainer() {
    const atBottom =
      this.thumbnailTarget.scrollTop >= this.thumbnailTarget.scrollHeight - this.thumbnailTarget.clientHeight;
    if (atBottom) return;

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.thumbnailTarget.scrollTo({
        top: this.thumbnailTarget.scrollHeight,
        behavior: "smooth",
      });
    }, 500);
  }

  addPdfPreview(file) {
    const container = document.createElement("div");
    container.classList.add("flex", "h-40", "rounded-sm", "bg-base-200", "p-4");

    const iconAndFilename = document.createElement("div");
    iconAndFilename.classList.add("flex", "items-center", "gap-2");

    iconAndFilename.innerHTML = `
    <svg class="w-4 h-4 p-2 sm:p-0 svg-inline--fa" type="regular" viewBox="0 0 512 512">
      <use href="#solid-file-pdf"></use>
    </svg>
    <span>${file.name}</span>
    `;

    container.appendChild(iconAndFilename);

    this.thumbnailTarget.appendChild(container);
    this.thumbnailTarget.classList.remove("hidden");
  }

  async connect() {
    // If there's no form, don't do anything. The message input sometimes doesn't have a form when it's shared
    // read-only.
    if (this.hasFormTarget) {
      await this.createUppy();
      this.setupPasteListeners();
    }
  }

  setupPasteListeners() {
    const inputs = this.dropzoneTarget.querySelectorAll('input[type="text"], textarea');

    inputs.forEach((input) => {
      input.addEventListener("paste", (event) => {
        const clipboardData = event.clipboardData || window.clipboardData;
        const items = clipboardData.items;

        if (items) {
          let hasFiles = false;

          for (let i = 0; i < items.length; i++) {
            if (items[i].kind === "file") {
              hasFiles = true;
              const file = items[i].getAsFile();
              console.log(items[i]);
              try {
                this.uppy.addFile({
                  source: "clipboard",
                  name: file.name || `pasted-file-${Date.now()}.${this.getExtensionFromMimeType(file.type)}`,
                  type: file.type,
                  data: file,
                });
              } catch (err) {
                if (!err.isRestriction) {
                  console.error(err);
                }
              }
            }
          }

          if (hasFiles) {
            event.preventDefault();
          }
        }
      });
    });
  }

  getExtensionFromMimeType(mimeType) {
    const mimeToExt = {
      "application/pdf": "pdf",
      "image/bmp": "bmp",
      "image/gif": "gif",
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/svg+xml": "svg",
      "image/tiff": "tiff",
      "image/webp": "webp",
    };

    return mimeToExt[mimeType] || "bin";
  }

  openDialog(event) {
    event.preventDefault();
    event.stopPropagation();
    this.fileTarget.click();
  }

  onBeforeFileAdded(currentFile) {
    if (!this.allowedFileTypesValue.includes(currentFile.type)) {
      alert(`${currentFile.name} could not be uploaded. Supported formats: ${this.allowedFileTypesValue.join(", ")}`);
      return false;
    }

    // If file is already processed, return it as is
    // If file is not an image, return it as is bc CompressorJS does not support it
    // If file is a gif, return it as is for now bc CompressorJS will remove animation
    if (currentFile.meta?.isProcessed || !currentFile.type.startsWith("image") || currentFile.type == "image/gif")
      return currentFile;

    // Disable upload button during conversion/compression
    this.disableUploadButton();

    // If the file is a heic or heif, convert it to a jpeg before compressing
    if (currentFile.type == "image/heic" || currentFile.type == "image/heif") {
      this.convertHeicToJpeg(currentFile).then((file) => {
        this.compressImage(file);
      });
    } else {
      this.compressImage(currentFile.data);
    }
    return false;
  }

  convertHeicToJpeg(currentFile) {
    return currentFile.data.arrayBuffer().then((buffer) => {
      const blob = new Blob([buffer]);
      return heic2any({
        blob: blob,
        toType: "image/jpeg",
      }).then((result) => {
        // Add back filename with new extension
        const fileName = currentFile.name.replace(/\.[^/.]+$/, ""); // removes only the last extension
        return new File([result], `${fileName}.jpg`, { type: "image/jpeg" });
      });
    });
  }

  compressImage(file) {
    return new Compressor(file, {
      quality: 0.8,
      maxWidth: 2048,
      maxHeight: 2048,
      mimeType: "image/jpeg",
      success: (compressedFile) => {
        this.uppy.addFile({
          name: compressedFile.name,
          type: compressedFile.type,
          data: compressedFile,
          meta: { ...compressedFile.meta, isProcessed: true },
        });
      },
      error: (err) => {
        alert(err.message);
        console.error(err.message);
      },
    });
  }

  disableUploadButton() {
    this.uploadButtonTarget.disabled = true;
    this.uploadButtonTarget.classList.add("du-btn-disabled");
  }
}
